import { useQuery } from "@apollo/client/react";
import { LatestCheckoutDocument } from "@repo/graphql/documents/subscription/query.generated";
import { SubscriptionIntentStatus } from "@repo/graphql/gql/graphql";
import { useEffect, useMemo, useState } from "react";

const POLL_INTERVAL_MS = 3000;
const MAX_WAIT_MS = 30000;

export type PaymentReturnStatus =
  "idle" | "verifying" | "completed" | "unconfirmed" | "notFound";

type PaymentReturnState = {
  status: PaymentReturnStatus;
  planName: string | null;
};

/**
 * Verifies a paid checkout return (onboarding or an in-app upgrade): nothing
 * ever flips a subscription intent to FAILED today, and an abandoned/declined
 * checkout stays PENDING forever,
 * so we poll for up to `MAX_WAIT_MS` and treat a still-pending intent as
 * "unconfirmed" past that point rather than waiting indefinitely.
 */
export const usePaymentReturnStatus = (
  active: boolean,
  reference: string | undefined,
): PaymentReturnState => {
  const [timedOut, setTimedOut] = useState(false);

  const { data, error, stopPolling } = useQuery(LatestCheckoutDocument, {
    variables: { reference },
    fetchPolicy: "network-only",
    pollInterval: active && !timedOut ? POLL_INTERVAL_MS : 0,
    skip: !active,
  });

  useEffect(() => {
    if (!active) return;
    setTimedOut(false);
    const timer = setTimeout(() => setTimedOut(true), MAX_WAIT_MS);
    return () => clearTimeout(timer);
  }, [active, reference]);

  const intent = data?.subscription.latestCheckout ?? null;
  const isPending = intent?.status === SubscriptionIntentStatus.Pending;

  useEffect(() => {
    if (!active) return;
    // Only once an answer is in: before the first result `isPending` is false
    // too, and stopping then would never poll at all.
    if (error || timedOut || (data !== undefined && !isPending)) stopPolling();
  }, [active, data, isPending, timedOut, error, stopPolling]);

  const status = useMemo<PaymentReturnStatus>(() => {
    if (!active) return "idle";
    // A poll that errors or never answers must not leave the page waiting
    // (and whatever it gates disabled) forever.
    if (data === undefined) {
      return error || timedOut ? "unconfirmed" : "verifying";
    }
    if (!intent) return "notFound";
    if (intent.status === SubscriptionIntentStatus.Completed)
      return "completed";
    if (intent.status === SubscriptionIntentStatus.Failed) return "unconfirmed";
    return timedOut ? "unconfirmed" : "verifying";
  }, [active, data, error, intent, timedOut]);

  return { status, planName: intent?.planName ?? null };
};
